import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography,
  IconButton,
  Modal,
  TextField,
  Snackbar,
  Alert as MuiAlert,
  MenuItem,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getMatchLiveData,
  PushMatchLiveData,
  updateLiveTaggingStatus,
} from "../config/mainRequest";
import { useParams } from "react-router-dom";

import { useSelector } from "react-redux";

import DeleteIcon from "@mui/icons-material/Delete";

const stats = {
  passing: "Passing",
  assists: "Assists",
  shots_off_target: "Shots off Target",
  own_goal: "Own Goal",
  fouls: "Fouls",
  chances_created: "Chances Created",
  shots_on_target: "Shots on Target",
  corners: "Corners",
  offside: "Offside",
  penalty: "Penalty",
  goal: "Goal",
  yellow_card: "Yellow Card",
  red_card: "Red Card",
};

function LiveTaggingBoard() {
  const { matchId } = useParams();
  const { token } = useSelector((state) => ({
    token: state.auth.token,
  }));

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [counts, setCounts] = useState({
    A: Object.fromEntries(Object.values(stats).map((label) => [label, 0])),
    B: Object.fromEntries(Object.values(stats).map((label) => [label, 0])),
  });
  const [cardDetails, setCardDetails] = useState({
    A: {
      goal: [],
      yellow_card: [],
      red_card: [],
      penalty: [],
      own_goal: [],
    },
    B: {
      goal: [],
      yellow_card: [],
      red_card: [],
      penalty: [],
      own_goal: [],
    },
  });
  const [openModalMarkComplete, setOpenModalMarkComplete] = useState(false);
  const [openModalCounterControl, setOpenModalCounterControl] = useState(false);
  const [modalStatKey, setModalStatKey] = useState("");
  const [modalTeam, setModalTeam] = useState("");
  const [modalData, setModalData] = useState([]);
  const [liveMatchData, setLiveMatchData] = useState(null);
  const [isAddNew, setIsAddNew] = useState(false);
  const [hasPushedFirstTime, setHasPushedFirstTime] = useState(false);

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  useEffect(() => {
    const fetchLiveData = async () => {
      const response = await getMatchLiveData({ matchId });
      if (response) {
        setLiveMatchData(response);

        const teamA = response?.team_a_details?.live_analysis_data;
        const teamB = response?.team_b_details?.live_analysis_data;

        setCardDetails({
          A: {
            goal: teamA?.goal?.details || [],
            yellow_card: teamA?.yellow_card?.details || [],
            red_card: teamA?.red_card?.details || [],
            penalty: teamA?.penalty?.details || [],
            own_goal: teamA?.own_goal?.details || [],
          },
          B: {
            goal: teamB?.goal?.details || [],
            yellow_card: teamB?.yellow_card?.details || [],
            red_card: teamB?.red_card?.details || [],
            penalty: teamB?.penalty?.details || [],
            own_goal: teamB?.own_goal?.details || [],
          },
        });
      }
    };

    if (matchId) {
      fetchLiveData();
    }
  }, [matchId]);

  const isAllZero = () => {
    const teamKeys = ["A", "B"];

    return teamKeys.every((team) => {
      const teamStats = counts?.[team] || {};

      return Object.keys(teamStats).every((key) => teamStats[key] === 0);
    });
  };

  const pushLiveData = async () => {
    if (isAllZero()) return;

    const getTeamStats = (teamKey) => {
      const stats = { ...counts[teamKey] };

      ["goal", "yellow_card", "red_card", "penalty", "own_goal"].forEach(
        (key) => {
          stats[key] = {
            count: counts[teamKey][key],
            details: cardDetails[teamKey]?.[key] || [],
          };
        }
      );

      return stats;
    };

    const teamAStats = getTeamStats("A");
    const teamBStats = getTeamStats("B");

    await PushMatchLiveData({
      matchId,
      teamAStats,
      teamBStats,
    });

    if (!hasPushedFirstTime) {
      try {
        const res = await updateLiveTaggingStatus({
          match_id: matchId,
          live_tagging_status: 2,
          token: token,
        });

        if (res?.response_message !== "") {
          setHasPushedFirstTime(true);
        }
      } catch (error) {
        console.error("Error updating tagging status:", error);
      }
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      pushLiveData();
    }, 15000);

    return () => clearInterval(interval);
  }, [counts, matchId, hasPushedFirstTime]);

  useEffect(() => {
    if (liveMatchData) {
      const teamAStats = parseStatsFromLiveData(
        liveMatchData.team_a_details.live_analysis_data
      );
      const teamBStats = parseStatsFromLiveData(
        liveMatchData.team_b_details.live_analysis_data
      );

      setCounts({
        A: teamAStats,
        B: teamBStats,
      });
    }
  }, [liveMatchData]);

  const handleUpdateClick = async () => {
    const getTeamStats = (teamKey) => {
      const stats = { ...counts[teamKey] };

      ["goal", "yellow_card", "red_card", "penalty", "own_goal"].forEach(
        (key) => {
          stats[key] = {
            count: counts[teamKey][key],
            details: cardDetails[teamKey]?.[key] || [],
          };
        }
      );

      return stats;
    };

    const teamAStats = getTeamStats("A");
    const teamBStats = getTeamStats("B");

    try {
      const res = await PushMatchLiveData({
        matchId,
        teamAStats,
        teamBStats,
      });

      if (res?.response_data === true) {
        showSnackbar("Data updated Successfully!", "success");
      } else {
        alert("Something went wrong while saving.");
      }
    } catch (error) {
      console.error("Update failed:", error);
      alert("An error occurred while saving data.");
    }
  };

  const parseStatsFromLiveData = (liveData) => {
    const result = {};

    Object.keys(stats).forEach((statKey) => {
      const statValue = liveData[statKey];

      if (typeof statValue === "object" && statValue?.count !== undefined) {
        result[statKey] = statValue.count;
      } else {
        result[statKey] = statValue || 0;
      }
    });

    return result;
  };

  const handleIncrement = (statKey, team) => {
    if (
      ["goal", "yellow_card", "red_card", "penalty", "own_goal"].includes(
        statKey
      )
    ) {
      handleOpenModalCounterControl(statKey, team, true);
    } else {
      setCounts((prev) => ({
        ...prev,
        [team]: {
          ...prev[team],
          [statKey]: (prev[team][statKey] || 0) + 1,
        },
      }));
    }
  };

  const handleDecrement = (statKey, team) => {
    setCounts((prev) => ({
      ...prev,
      [team]: {
        ...prev[team],
        [statKey]: Math.max(0, (prev[team][statKey] || 0) - 1),
      },
    }));
  };

  const handleOpenModal = () => {
    setOpenModalMarkComplete(true);
  };

  const handleCloseModal = () => {
    setOpenModalMarkComplete(false);
  };

  const handleAccept = async () => {
    try {
      const res = await updateLiveTaggingStatus({
        match_id: matchId,
        live_tagging_status: 3,
        token: token,
      });

      if (res?.response_message !== "") {
        showSnackbar("Marked Completed!", "success");
        setOpenModalMarkComplete(false);
      }
    } catch (error) {
      console.error("Error updating tagging status:", error);
    }
  };

  const handleOpenModalCounterControl = (statKey, team, isAddNew) => {
    setModalStatKey(statKey);
    setModalTeam(team);
    setIsAddNew(isAddNew);

    const currentDetails = cardDetails[team]?.[statKey] || [];

    const updatedDetails = isAddNew
      ? [{ time: "", jersey_no: "" }]
      : [...currentDetails];

    setModalData(JSON.parse(JSON.stringify(updatedDetails)));
    setOpenModalCounterControl(true);
  };

  const handleCloseModalCounterControl = () => {
    setOpenModalCounterControl(false);
  };

  const BoxItem = ({ statKey, team }) => {
    const label = stats[statKey];

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid #D1D5DB",
          backgroundColor: "#F3F4F6",
          padding: "8px 12px",
          borderRadius: "8px",
          flexGrow: 1,
          minWidth: "0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {["goal", "yellow_card", "red_card", "penalty", "own_goal"].includes(
            statKey
          ) && (
            <IconButton
              onClick={() =>
                handleOpenModalCounterControl(statKey, team, false)
              }
              sx={{
                padding: 0,
                color: "#1A56DB",
                fontSize: "4px",
              }}
            >
              <EditNoteIcon fontSize="small" />
            </IconButton>
          )}
          <Typography sx={{ fontSize: "10px", fontWeight: "regular" }}>
            {label}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              minWidth: "24px",
              height: "24px",
              backgroundColor: "#E5E7EB",
              borderColor: "#D1D5DB",
              color: "#111928",
              fontSize: "12px",
              fontWeight: "bold",
              padding: 0,
              lineHeight: 1,
              minHeight: "auto",
            }}
            onClick={() => handleDecrement(statKey, team)}
            disabled={[
              "goal",
              "yellow_card",
              "red_card",
              "penalty",
              "own_goal",
            ].includes(statKey)}
          >
            -
          </Button>

          <Box
            sx={{
              minWidth: "28px",
              height: "24px",
              backgroundColor: team === "A" ? "#1A56DB" : "#FF5A1F",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: "bold",
            }}
          >
            {counts[team]?.[statKey] ?? 0}
          </Box>

          <Button
            variant="outlined"
            sx={{
              minWidth: "24px",
              height: "24px",
              backgroundColor: "#E5E7EB",
              borderColor: "#D1D5DB",
              color: "#111928",
              fontSize: "12px",
              fontWeight: "bold",
              padding: 0,
              lineHeight: 1,
              minHeight: "auto",
            }}
            onClick={() => handleIncrement(statKey, team)}
          >
            +
          </Button>
        </Box>
      </Box>
    );
  };

  const MarkCompleteModal = () => {
    return (
      <Modal open={openModalMarkComplete} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "20px",
            boxShadow: 24,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Mark Complete
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ marginTop: "8px", marginBottom: "16px" }}
          >
            Are you sure you want to mark this live match analysis as complete?
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              paddingX: "20px",
            }}
          >
            <Button
              variant="outlined"
              sx={{
                flex: 1,
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "#1F2A37",
                backgroundColor: "#fff",
                color: "#1F2A37",
                fontWeight: "medium",
                fontSize: "14px",
              }}
              onClick={handleCloseModal}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                flex: 1,
                textTransform: "none",
                borderRadius: "8px",
                backgroundColor: "#1A56DB",
                color: "#fff",
                fontWeight: "medium",
                fontSize: "14px",
              }}
              onClick={handleAccept}
            >
              I accept
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  const GenericSnackbar = ({
    open,
    onClose,
    message,
    severity = "success",
    duration = 3000,
  }) => {
    return (
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={onClose} severity={severity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    );
  };

  const showSnackbar = (msg, severity = "success") => {
    setSnackbar({ open: true, message: msg, severity });
  };

  return (
    <Box sx={{ minHeight: "100vh", overflowY: "auto" }}>
      <CounterControlModal
        openModalCounterControl={openModalCounterControl}
        modalData={modalData}
        handleCloseModalCounterControl={handleCloseModalCounterControl}
        isAddNew={isAddNew}
        modalStatKey={modalStatKey}
        cardDetails={cardDetails}
        modalTeam={modalTeam}
        setCardDetails={setCardDetails}
        setCounts={setCounts}
      />
      <Box
        sx={{
          backgroundColor: "#000",
          color: "#fff",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 1000,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 1,
          }}
        >
          <Typography
            variant="body1"
            fontWeight="medium"
            color="#3F83F8"
            noWrap
          >
            {matchId}
          </Typography>

          <Typography variant="body1" fontWeight="medium" color="#fff" noWrap>
            {`${liveMatchData?.team_a_details?.team_name} vs ${liveMatchData?.team_b_details?.team_name}`}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          fontWeight="regular"
          color="#9CA3AF"
          sx={{ flexShrink: 0 }}
        >
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
      </Box>

      <Container
        sx={{
          display: "flex",
          flexDirection: "row",
          marginTop: "40px",
          justifyContent: "space-between",
          gap: "10px",
          padding: "20px",
        }}
      >
        {/* Box 1 */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#fff",
            boxShadow: 3,
            borderRadius: "8px",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium" color="#111928">
              {liveMatchData?.team_a_details?.team_name}
            </Typography>
            <Typography variant="subtitle2" fontWeight="medium" color="#9CA3AF">
              Team A
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: "#E5E7EB", marginY: "8px" }} />

          <Grid
            container
            spacing={0.8}
            sx={{
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {Object.keys(stats).map((statKey) => (
              <Grid item xs={6} key={statKey} sx={{ display: "flex" }}>
                <BoxItem statKey={statKey} team="A" />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Box 2 */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#fff",
            boxShadow: 3,
            borderRadius: "8px",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" fontWeight="medium" color="#9CA3AF">
              Team B
            </Typography>
            <Typography variant="subtitle1" fontWeight="medium" color="#111928">
              {liveMatchData?.team_b_details?.team_name}
            </Typography>
          </Box>

          <Divider sx={{ backgroundColor: "#E5E7EB", marginY: "8px" }} />

          <Grid
            container
            spacing={0.8}
            sx={{
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {Object.keys(stats).map((statKey) => (
              <Grid item xs={6} key={statKey} sx={{ display: "flex" }}>
                <BoxItem statKey={statKey} team="B" />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      <Box
        sx={{
          display: "flex",
          gap: "10px",
          width: "100%",
          padding: "0 20px",
          boxSizing: "border-box",
          marginBottom: "10px",
        }}
      >
        <Button
          onClick={handleUpdateClick}
          variant="outlined"
          fullWidth
          sx={{
            borderColor: "#1F2A37",
            backgroundColor: "#fff",
            color: "#1F2A37",
            fontWeight: "medium",
            fontSize: "14px",
            padding: "8px 12px",
            textTransform: "none",
            height: "36px",
            borderRadius: "8px",
          }}
        >
          Update
        </Button>

        <Button
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: "#1A56DB",
            color: "#fff",
            fontWeight: "medium",
            fontSize: "14px",
            padding: "8px 12px",
            textTransform: "none",
            height: "36px",
            "&:hover": {
              backgroundColor: "#174BB6",
            },
            borderRadius: "8px",
          }}
          onClick={handleOpenModal}
        >
          Mark as Complete
        </Button>
      </Box>

      <MarkCompleteModal />

      <GenericSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

const CounterControlModal = ({
  openModalCounterControl,
  modalData,
  handleCloseModalCounterControl,
  isAddNew,
  modalStatKey,
  cardDetails,
  modalTeam,
  setCardDetails,
  setCounts,
}) => {
  const [localModalData, setLocalModalData] = useState([]);

  const convertTimeToMinutes = (hhmmss) => {
    if (!hhmmss) return "";
    const [hours, minutes] = hhmmss.split(":");
    return String(parseInt(hours, 10) * 60 + parseInt(minutes, 10));
  };
  const isUpdateDisabled = localModalData.some(
    (item) =>
      isNaN(parseInt(item.time)) ||
      isNaN(parseInt(item.jersey_no)) ||
      parseInt(item.time) <= 0 ||
      parseInt(item.jersey_no) <= 0
  );

  const formatTime = (minuteStr) => {
    const mins = parseInt(minuteStr || "0", 10);
    const hours = Math.floor(mins / 60)
      .toString()
      .padStart(2, "0");
    const minutes = (mins % 60).toString().padStart(2, "0");
    return `${hours}:${minutes}:00`;
  };

  const handleDeleteItem = (index) => {
    setLocalModalData((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (openModalCounterControl) {
      const converted = modalData.map((item) => ({
        ...item,
        time: convertTimeToMinutes(item.time), // hh:mm:ss -> minutes
      }));
      setLocalModalData(converted);
    } else {
      setLocalModalData([]);
    }
  }, [openModalCounterControl, modalData]);

  return (
    <Modal
      open={openModalCounterControl}
      onClose={handleCloseModalCounterControl}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: "8px",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" fontWeight="bold" mb={2}>
          {modalStatKey
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}{" "}
          Details
        </Typography>

        {localModalData.map((item, index) => {
          const timeError = !item.time || parseInt(item.time) <= 0;
          const jerseyError = !item.jersey_no || parseInt(item.jersey_no) <= 0;

          return (
            <Box key={index} mb={2}>
              {index !== 0 && <Divider sx={{ mb: 2 }} />}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle2" fontWeight="medium" mb={1}>
                  {`${stats[modalStatKey] || "Event"}${
                    isAddNew ? "" : ` ${index + 1}`
                  } Time`}
                </Typography>

                {!isAddNew && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteItem(index)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter time in mins (e.g. 45, 75, 120)"
                value={item.time}
                error={timeError}
                helperText={timeError ? "Enter valid time > 0" : ""}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "").slice(0, 3);
                  if (value && parseInt(value, 10) > 120) value = "120";
                  setLocalModalData((prev) =>
                    prev.map((el, i) =>
                      i === index ? { ...el, time: value } : el
                    )
                  );
                }}
                inputProps={{ maxLength: 3 }}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "#F9FAFB",
                    fontSize: "12px",
                  },
                  "& input": { fontSize: "12px" },
                  "& input::placeholder": {
                    color: "#6B7280",
                    opacity: 1,
                    fontSize: "12px",
                  },
                }}
              />

              <Typography variant="subtitle2" fontWeight="medium" mt={2} mb={1}>
                Jersey No.
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Enter jersey number (e.g. 7, 9, 11)"
                value={item.jersey_no}
                error={jerseyError}
                helperText={jerseyError ? "Enter valid jersey no > 0" : ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setLocalModalData((prev) =>
                    prev.map((el, i) =>
                      i === index ? { ...el, jersey_no: value } : el
                    )
                  );
                }}
                inputProps={{ maxLength: 2 }}
                sx={{
                  "& .MuiInputBase-root": {
                    backgroundColor: "#F9FAFB",
                    fontSize: "12px",
                  },
                  "& input": { fontSize: "12px" },
                  "& input::placeholder": {
                    color: "#6B7280",
                    opacity: 1,
                    fontSize: "12px",
                  },
                }}
              />

              {modalStatKey === "penalty" && (
                <>
                  <Typography
                    variant="subtitle2"
                    fontWeight="medium"
                    mt={2}
                    mb={1}
                  >
                    Penalty Type
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={item.alias || "in_match"}
                    onChange={(e) => {
                      const value = e.target.value;
                      setLocalModalData((prev) =>
                        prev.map((el, i) =>
                          i === index ? { ...el, alias: value } : el
                        )
                      );
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "#F9FAFB",
                        fontSize: "12px",
                      },
                      "& select": {
                        fontSize: "12px",
                      },
                    }}
                  >
                    <MenuItem value="in_match">In Match</MenuItem>
                    <MenuItem value="shootouts">Shootouts</MenuItem>
                  </TextField>
                </>
              )}
            </Box>
          );
        })}

        <Box display="flex" width="100%" mt={2}>
          <Button
            disabled={isUpdateDisabled}
            variant="contained"
            sx={{
              textTransform: "none",
              flex: 1,
              backgroundColor: "#1A56DB",
            }}
            onClick={() => {
              const rawFormattedData = localModalData.map((item) => {
                const formatted = {
                  time: formatTime(item.time),
                  jersey_no: String(Number(item.jersey_no)),
                };

                if (modalStatKey === "penalty") {
                  formatted.alias = item.alias || "in_match";
                }

                return formatted;
              });

              const validFormattedData = rawFormattedData.filter(
                (item) => item.time !== "00:00:00" && item.jersey_no !== "0"
              );

              const existing = cardDetails[modalTeam]?.[modalStatKey] || [];

              let newData;

              if (isAddNew) {
                // ADDITION MODE
                newData = [...existing, ...validFormattedData];
              } else {
                // EDIT MODE — just use what's currently present in localModalData
                newData = validFormattedData;
              }

              const updatedCardDetails = {
                ...cardDetails,
                [modalTeam]: {
                  ...cardDetails[modalTeam],
                  [modalStatKey]: newData,
                },
              };

              setCardDetails(updatedCardDetails);

              const isKeyStat = [
                "goal",
                "yellow_card",
                "red_card",
                "penalty",
                "own_goal",
              ].includes(modalStatKey);

              if (isKeyStat) {
                setCounts((prev) => {
                  return {
                    ...prev,
                    [modalTeam]: {
                      ...prev[modalTeam],
                      [modalStatKey]: newData.length,
                    },
                  };
                });
              }

              handleCloseModalCounterControl();
            }}
          >
            Update
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default LiveTaggingBoard;
